'use client'

import { inPracticeZone } from '@tebuto/functions/practice-time'
import { useComponentTranslation } from '@tebuto/functions/translation.components'
import Heading from '@tebuto/typography/Heading'
import { BookingWidgetContext } from '@tebuto/widget/booking-widget/context'
import { DateTime } from 'luxon'
import { useContext, useEffect, useMemo, useState } from 'react'
import BookingBackButton from './BookingBackButton'
import Calendar from './calendar/Calendar'
import { calendarCellDayKey, calendarCellForEvent, eventDayKey, eventWeekday } from './calendar/calendar-day'
import { BookingCalendarContext } from './calendar/context'
import EventNotAvailableDialog from './calendar/EventNotAvailableDialog'
import Events from './calendar/Events'
import FilterArea from './calendar/filters/FilterArea'
import Legend from './calendar/Legend'
import QuickFilters from './calendar/QuickFilters'
import { arraysEqual, isFullSelection, reconcileSelectedIds, uniqueNumberIds } from './calendar/selection-utils'
import { BookingContext } from './context'
import { resolveEventLocation } from './functions'
import { EventLocation, type ParsedBookableEvent } from './types'

const EVENT_LOCATION_ORDER = [EventLocation.ONSITE, EventLocation.VIRTUAL, EventLocation.NOT_FIXED]

function getEventCityLabel(event: ParsedBookableEvent): string | null {
    const city = event.therapist?.address?.city
    if (!city?.name) {
        return null
    }

    return city.name
}

export default function BookingCalendar() {
    const { t } = useComponentTranslation()
    const {
        setBookingEvent,
        setNavigationDirection,
        setState,
        refreshEvents,
        events,
        categories,
        therapists,
        showQuickFilters,
        showLocationQuickFilter,
        fixedCategoryId,
        selectedCategoryId,
        setSelectedCategoryId
    } = useContext(BookingContext)

    const effectiveCategoryLock = fixedCategoryId ?? selectedCategoryId ?? undefined
    const resolveCalendarCategoryId = (event: ParsedBookableEvent): number => {
        if (effectiveCategoryLock === undefined) {
            return event.categoryId
        }
        if (event.categoryId === effectiveCategoryLock) {
            return event.categoryId
        }
        if (event.ruleCategoryIds?.includes(effectiveCategoryLock)) {
            return effectiveCategoryLock
        }
        return event.categoryId
    }
    const showBackToCategorySelection = selectedCategoryId !== null && !fixedCategoryId
    const { configuredCategories, configuredTherapists } = useContext(BookingWidgetContext)
    const configuredCategoryIdSet = useMemo(
        () => new Set((configuredCategories ?? []).map((category) => category.id)),
        [configuredCategories]
    )
    const showWeekend = !!events.find((event) => eventWeekday(event.startDatetime) >= 6)

    // Check if this is a multi-user setup
    // Either: multiple therapists have events OR showQuickFilters is explicitly enabled (provider filter)
    const isMultiUser = useMemo(
        () => showQuickFilters || (therapists && Object.keys(therapists).length > 1),
        [therapists, showQuickFilters]
    )

    // States and filters
    const [selectedDate, setSelectedDate] = useState<DateTime<true>>(DateTime.now())
    const [noEventsForFilter, setNoEventsForFilter] = useState<boolean>(false)
    const [eventsForSelectedDate, setEventsForSelectedDate] = useState<ParsedBookableEvent[]>([])
    const [earliestStart, setEarliestStart] = useState<DateTime<true>>(
        DateTime.fromObject({ hour: 0, minute: 0 }) as DateTime<true>
    )
    const [latestEnd, setLatestEnd] = useState<DateTime<true>>(
        DateTime.fromObject({ hour: 23, minute: 59 }) as DateTime<true>
    )
    const [availableCategoryIDs, setAvailableCategoryIDs] = useState<number[]>([])
    const [selectedCategoryIDs, setSelectedCategoryIDs] = useState<number[]>([])
    const [availableTherapistIDs, setAvailableTherapistIDs] = useState<number[]>([])
    const [selectedTherapistIDs, setSelectedTherapistIDs] = useState<number[]>([])
    const [availableLocations, setAvailableLocations] = useState<EventLocation[]>([])
    const [selectedLocations, setSelectedLocations] = useState<EventLocation[]>([])
    const [availableCities, setAvailableCities] = useState<string[]>([])
    const [selectedCities, setSelectedCities] = useState<string[]>([])
    const [selectedWeekdays, setSelectedWeekdays] = useState<number[]>([])
    const [availableWeekdays, setAvailableWeekdays] = useState<number[]>([])
    const [filteredEvents, setFilteredEvents] = useState<ParsedBookableEvent[]>([])
    const [isEventNotAvailableDialogOpen, setIsEventNotAvailableDialogOpen] = useState(false)

    // Filters
    const [filtersAreOpen, setFiltersAreOpen] = useState(false)

    const noEventsAvailable = useMemo(() => events.length === 0, [events])

    const therapistsWithEventsMap = useMemo(() => {
        const map: { [therapistId: number]: Set<number> } = {}
        events.forEach((event) => {
            if (!event.therapist) {
                return
            }

            const calendarCategoryId = resolveCalendarCategoryId(event)
            const category = categories[calendarCategoryId]
            if (!category) {
                return
            }

            if (configuredCategoryIdSet.size > 0 && !configuredCategoryIdSet.has(calendarCategoryId)) {
                return
            }

            if (effectiveCategoryLock !== undefined && calendarCategoryId !== effectiveCategoryLock) {
                return
            }

            if (!map[event.therapist.id]) {
                map[event.therapist.id] = new Set()
            }
            map[event.therapist.id].add(calendarCategoryId)
        })
        return map
    }, [events, categories, configuredCategoryIdSet, effectiveCategoryLock])

    // Build a map of which therapists offer which categories
    const therapistCategoryMap = useMemo(() => {
        const map: { [therapistId: number]: Set<number> } = {}
        events.forEach((event) => {
            if (event.therapist) {
                const calendarCategoryId = resolveCalendarCategoryId(event)
                const category = categories[calendarCategoryId]
                if (!category) {
                    return
                }

                if (selectedCategoryIDs.length > 0 && !selectedCategoryIDs.includes(calendarCategoryId)) {
                    return
                }

                if (configuredCategoryIdSet.size > 0 && !configuredCategoryIdSet.has(calendarCategoryId)) {
                    return
                }

                if (effectiveCategoryLock !== undefined && calendarCategoryId !== effectiveCategoryLock) {
                    return
                }

                if (!map[event.therapist.id]) {
                    map[event.therapist.id] = new Set()
                }
                map[event.therapist.id].add(calendarCategoryId)
            }
        })
        return map
    }, [events, selectedCategoryIDs, categories, configuredCategoryIdSet, effectiveCategoryLock])

    // Set available therapists and initial selection
    useEffect(() => {
        let nextIDs: number[]

        if (configuredTherapists && configuredTherapists.length > 0) {
            const fromConfigured = configuredTherapists.map((therapist) => therapist.id)
            const fromEvents = Object.keys(therapistsWithEventsMap)
                .map((id) => Number.parseInt(id, 10))
                .filter((id) => (therapistsWithEventsMap[id]?.size ?? 0) > 0)
            nextIDs = uniqueNumberIds([...fromConfigured, ...fromEvents])
        } else if (therapists) {
            const therapistIDs = Object.keys(therapists).map((id) => Number.parseInt(id, 10))
            nextIDs = uniqueNumberIds(therapistIDs.filter((id) => therapistsWithEventsMap[id]?.size))
        } else {
            return
        }

        setAvailableTherapistIDs((prev) => (arraysEqual(prev, nextIDs) ? prev : nextIDs))
        setSelectedTherapistIDs((prev) => reconcileSelectedIds(prev, nextIDs))
    }, [therapists, configuredTherapists, therapistsWithEventsMap])

    useEffect(() => {
        if (selectedDate) {
            setEventsForSelectedDate(
                filteredEvents
                    ?.filter((event) => eventDayKey(event.startDatetime) === calendarCellDayKey(selectedDate))
                    .sort((a, b) => (a.start > b.start ? 1 : -1)) as ParsedBookableEvent[]
            )
        }
    }, [filteredEvents, selectedDate])

    useEffect(() => {
        if (showWeekend) {
            setAvailableWeekdays([1, 2, 3, 4, 5, 6, 7])
            setSelectedWeekdays([1, 2, 3, 4, 5, 6, 7])
        } else {
            setAvailableWeekdays([1, 2, 3, 4, 5])
            setSelectedWeekdays([1, 2, 3, 4, 5])
        }
    }, [showWeekend])

    const allTherapistsSelected = isFullSelection(selectedTherapistIDs, availableTherapistIDs)
    const allCitiesSelected = selectedCities.length === availableCities.length

    function eventFilter(event: ParsedBookableEvent) {
        const calendarCategoryId = resolveCalendarCategoryId(event)
        const isConfiguredCategory =
            configuredCategoryIdSet.size === 0 || configuredCategoryIdSet.has(calendarCategoryId)
        const isCategorySelected =
            isConfiguredCategory &&
            (selectedCategoryIDs.length === 0 || selectedCategoryIDs.includes(calendarCategoryId))
        // When all therapists are selected, don't filter by therapist at all
        const isTherapistSelected =
            !isMultiUser ||
            !event.therapist ||
            allTherapistsSelected ||
            selectedTherapistIDs.length === 0 ||
            selectedTherapistIDs.includes(event.therapist.id)
        // Check if event location is in selected locations (if no locations selected, show all)
        const eventLocation = resolveEventLocation(event, categories)
        const isLocationSelected =
            selectedLocations.length > 0 && eventLocation ? selectedLocations.includes(eventLocation) : true
        const eventCityLabel = getEventCityLabel(event)
        const isCitySelected =
            allCitiesSelected ||
            selectedCities.length === 0 ||
            (eventCityLabel ? selectedCities.includes(eventCityLabel) : false)
        const isWeekdaySelected = selectedWeekdays.includes(eventWeekday(event.startDatetime))
        const isTimeSelected =
            inPracticeZone(event.startDatetime).toFormat('HH:mm') >= earliestStart.toFormat('HH:mm') &&
            inPracticeZone(event.endDatetime).toFormat('HH:mm') <= latestEnd.toFormat('HH:mm')

        return (
            isCategorySelected &&
            isTherapistSelected &&
            isLocationSelected &&
            isCitySelected &&
            isWeekdaySelected &&
            isTimeSelected
        )
    }

    useEffect(() => {
        if (events) {
            const filtered = events.filter(eventFilter)
            if (!filtered.length) {
                setNoEventsForFilter(true)
            } else {
                setNoEventsForFilter(false)
            }
            setFilteredEvents(filtered)
            jumpToNextFreeEvent(selectedDate, filtered)
        }
    }, [
        events,
        selectedDate,
        selectedCategoryIDs,
        selectedTherapistIDs,
        availableTherapistIDs,
        selectedLocations,
        selectedCities,
        availableCities,
        selectedWeekdays,
        earliestStart,
        latestEnd,
        isMultiUser,
        categories,
        configuredCategoryIdSet,
        configuredTherapists,
        allCitiesSelected
    ])

    // Compute available categories based on selected therapists (using useMemo to avoid infinite loops)
    const computedAvailableCategoryIDs = useMemo(() => {
        if (effectiveCategoryLock !== undefined) {
            return [effectiveCategoryLock]
        }

        const allCategoryIDs = Object.keys(categories || {}).map((id) => Number.parseInt(id, 10))

        if (!isMultiUser || !therapists || !categories) {
            if (configuredCategoryIdSet.size > 0) {
                const fromApi = allCategoryIDs.filter((categoryId) => configuredCategoryIdSet.has(categoryId))
                return uniqueNumberIds(fromApi.length > 0 ? fromApi : [...configuredCategoryIdSet])
            }
            return allCategoryIDs
        }

        let categoryIDs: number[]

        if (selectedTherapistIDs.length === 0 || isFullSelection(selectedTherapistIDs, availableTherapistIDs)) {
            categoryIDs = allCategoryIDs
        } else {
            const availableCategories = new Set<number>()
            selectedTherapistIDs.forEach((therapistId) => {
                const categoriesForTherapist = therapistCategoryMap[therapistId]
                if (categoriesForTherapist) {
                    categoriesForTherapist.forEach((categoryId) => availableCategories.add(categoryId))
                }
            })
            categoryIDs = Array.from(availableCategories)
        }

        const filtered =
            configuredCategoryIdSet.size > 0
                ? categoryIDs.filter((categoryId) => configuredCategoryIdSet.has(categoryId))
                : categoryIDs
        if (filtered.length > 0) {
            return uniqueNumberIds(filtered)
        }
        if (configuredCategoryIdSet.size > 0) {
            return uniqueNumberIds([...configuredCategoryIdSet])
        }
        return uniqueNumberIds(filtered)
    }, [
        selectedTherapistIDs,
        availableTherapistIDs,
        therapistCategoryMap,
        isMultiUser,
        therapists,
        categories,
        effectiveCategoryLock,
        configuredCategoryIdSet
    ])

    // Sync computed available IDs to state (only when they actually change)
    useEffect(() => {
        const uniqueComputed = uniqueNumberIds(computedAvailableCategoryIDs)
        setAvailableCategoryIDs((prev) => (arraysEqual(prev, uniqueComputed) ? prev : uniqueComputed))
        setSelectedCategoryIDs((prev) => {
            const uniquePrev = uniqueNumberIds(prev)
            const filtered = uniquePrev.filter((id) => uniqueComputed.includes(id))
            const next = uniqueNumberIds(filtered.length > 0 ? filtered : [...uniqueComputed])
            return arraysEqual(uniquePrev, next) && prev.length === uniquePrev.length ? prev : next
        })
    }, [computedAvailableCategoryIDs])

    const computedAvailableLocations = useMemo(() => {
        const locationSet = new Set<EventLocation>()

        events.forEach((event) => {
            const calendarCategoryId = resolveCalendarCategoryId(event)
            const isConfiguredCategory =
                configuredCategoryIdSet.size === 0 || configuredCategoryIdSet.has(calendarCategoryId)
            const isCategorySelected =
                isConfiguredCategory &&
                (selectedCategoryIDs.length === 0 || selectedCategoryIDs.includes(calendarCategoryId))
            const isTherapistSelected =
                !isMultiUser ||
                !event.therapist ||
                allTherapistsSelected ||
                selectedTherapistIDs.length === 0 ||
                selectedTherapistIDs.includes(event.therapist.id)

            if (!isCategorySelected || !isTherapistSelected) {
                return
            }

            const location = resolveEventLocation(event, categories)
            if (location) {
                locationSet.add(location)
            }
        })

        return EVENT_LOCATION_ORDER.filter((location) => locationSet.has(location))
    }, [
        allTherapistsSelected,
        categories,
        configuredCategoryIdSet,
        configuredTherapists,
        events,
        isMultiUser,
        selectedCategoryIDs,
        selectedTherapistIDs
    ])

    useEffect(() => {
        setAvailableLocations((prev) =>
            arraysEqual(prev, computedAvailableLocations) ? prev : computedAvailableLocations
        )
        setSelectedLocations((prev) => {
            const filtered = prev.filter((location) => computedAvailableLocations.includes(location))
            const next = filtered.length > 0 ? filtered : [...computedAvailableLocations]
            return arraysEqual(prev, next) ? prev : next
        })
    }, [computedAvailableLocations])

    const computedAvailableCities = useMemo(() => {
        const citySet = new Set<string>()

        events.forEach((event) => {
            const calendarCategoryId = resolveCalendarCategoryId(event)
            const isConfiguredCategory =
                configuredCategoryIdSet.size === 0 || configuredCategoryIdSet.has(calendarCategoryId)
            const isCategorySelected =
                isConfiguredCategory &&
                (selectedCategoryIDs.length === 0 || selectedCategoryIDs.includes(calendarCategoryId))
            const isTherapistSelected =
                !isMultiUser ||
                !event.therapist ||
                allTherapistsSelected ||
                selectedTherapistIDs.length === 0 ||
                selectedTherapistIDs.includes(event.therapist.id)

            if (!isCategorySelected || !isTherapistSelected) {
                return
            }

            const cityLabel = getEventCityLabel(event)
            if (cityLabel) {
                citySet.add(cityLabel)
            }
        })

        return [...citySet].sort((a, b) => a.localeCompare(b, 'de'))
    }, [
        allTherapistsSelected,
        categories,
        configuredCategoryIdSet,
        configuredTherapists,
        events,
        isMultiUser,
        selectedCategoryIDs,
        selectedTherapistIDs
    ])

    useEffect(() => {
        setAvailableCities((prev) => (arraysEqual(prev, computedAvailableCities) ? prev : computedAvailableCities))
        setSelectedCities((prev) => {
            const filtered = prev.filter((city) => computedAvailableCities.includes(city))
            const next = filtered.length > 0 ? filtered : [...computedAvailableCities]
            return arraysEqual(prev, next) ? prev : next
        })
    }, [computedAvailableCities])

    function nextPage() {
        const newDate = selectedDate.plus({ month: 1 }).startOf('month')
        setBookingEvent(undefined)
        setNavigationDirection(null)
        setSelectedDate(newDate as DateTime<true>)
        jumpToNextFreeEvent(newDate, events.filter(eventFilter))
    }

    function previousPage() {
        const newDate = selectedDate.minus({ month: 1 }).startOf('month')
        setBookingEvent(undefined)
        setNavigationDirection(null)
        setSelectedDate(newDate as DateTime<true>)
        jumpToNextFreeEvent(newDate, events.filter(eventFilter))
    }

    function jumpToNextFreeEvent(monthAnchor: DateTime, candidates?: ParsedBookableEvent[]) {
        if (!events) return
        const pool = candidates ?? events.filter(eventFilter)
        const monthStartKey = calendarCellDayKey(monthAnchor.startOf('month'))
        const monthKey = monthAnchor.toFormat('yyyy-MM')
        const sortedFromMonthStart = pool
            .filter((event) => eventDayKey(event.startDatetime) >= monthStartKey)
            .sort((a, b) => (a.start > b.start ? 1 : -1))

        const monthOnly = sortedFromMonthStart.filter((event) => eventDayKey(event.startDatetime).startsWith(monthKey))

        const isPagingMonthAnchor = monthAnchor.equals(monthAnchor.startOf('month'))
        const monthEvents = isPagingMonthAnchor ? monthOnly : monthOnly.length > 0 ? monthOnly : sortedFromMonthStart

        if (!monthEvents.length) {
            setNoEventsForFilter(true)
            return
        }

        setNoEventsForFilter(false)

        const anchorStillHasSlots = monthEvents.some(
            (event) => eventDayKey(event.startDatetime) === calendarCellDayKey(monthAnchor)
        )
        if (anchorStillHasSlots) {
            return
        }

        setSelectedDate(calendarCellForEvent(monthEvents[0].startDatetime))
    }

    return (
        <BookingCalendarContext.Provider
            value={{
                nextPage,
                previousPage,
                noEventsAvailable,
                earliestStart,
                setEarliestStart,
                latestEnd,
                setLatestEnd,
                categories,
                therapists: therapists || {},
                selectedDate,
                setSelectedDate,
                filtersAreOpen,
                setFiltersAreOpen,
                filteredEvents,
                setFilteredEvents,
                eventsForSelectedDate,
                setEventsForSelectedDate,
                availableLocations,
                setAvailableLocations,
                selectedLocations,
                setSelectedLocations,
                availableCities,
                setAvailableCities,
                selectedCities,
                setSelectedCities,
                noEventsForFilter,
                setNoEventsForFilter,
                availableWeekdays,
                setAvailableWeekdays,
                selectedWeekdays,
                setSelectedWeekdays,
                availableCategoryIDs,
                setAvailableCategoryIDs,
                selectedCategoryIDs,
                setSelectedCategoryIDs,
                availableTherapistIDs,
                setAvailableTherapistIDs,
                selectedTherapistIDs,
                setSelectedTherapistIDs,
                isEventNotAvailableDialogOpen,
                setIsEventNotAvailableDialogOpen,
                showWeekend,
                isMultiUser,
                showQuickFilters: !!showQuickFilters,
                showLocationQuickFilter: !!showLocationQuickFilter,
                fixedCategoryId: effectiveCategoryLock
            }}
        >
            <div
                className="grid flex-1 min-h-0 gap-2 grid-cols-1 lg:h-full lg:grid-cols-5 lg:grid-rows-1 lg:gap-0"
                data-testid="booking-calendar"
            >
                <div className="lg:col-span-2 min-h-0 lg:pb-4">
                    <Calendar />
                    <div>
                        <div className="hidden md:block mt-6">
                            <Legend />
                        </div>
                        <div className="mt-6">
                            <FilterArea />
                        </div>
                    </div>
                </div>
                <div className="flex min-h-0 lg:col-span-3">
                    <div className="min-h-0 lg:border-l widget-border lg:pl-10 lg:ml-8 lg:overflow-hidden lg:mb-4" />
                    <div className="flex flex-1 flex-col min-h-0 lg:overflow-hidden">
                        {showBackToCategorySelection && (
                            <BookingBackButton
                                onClick={() => {
                                    setSelectedCategoryId(null)
                                    setNavigationDirection('back')
                                    setState('category-selection')
                                }}
                                label={t('booking.bookingCalendar.backToCategories')}
                                data-testid="back-to-categories"
                            />
                        )}
                        {(showQuickFilters || showLocationQuickFilter) && <QuickFilters />}
                        <Heading level={4} className="text-lg widget-text-primary font-semibold">
                            {t('booking.bookingCalendar.freeEvents')}
                        </Heading>
                        <p className="text-sm widget-text-secondary">
                            {selectedDate?.setLocale('de').toFormat('DDDD')}
                        </p>
                        <div className="grid gap-2 mt-4 pb-4 lg:mt-8 lg:overflow-y-auto lg:flex-1 lg:min-h-0 content-start widget-scrollbar">
                            <Events />
                            <EventNotAvailableDialog refresh={refreshEvents} />
                        </div>
                    </div>
                </div>
            </div>
        </BookingCalendarContext.Provider>
    )
}
